/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
/* eslint-disable max-lines-per-function */
/* eslint-disable no-invalid-this */
import {html, nothing, LitElement} from 'lit';
import {ref, createRef} from 'lit/directives/ref.js';
import {classMap} from 'lit/directives/class-map.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {createFocusTrap} from 'focus-trap';
import styles from './g-modal.component.scss?as=stylesheet';
import AnimationAwaiter from 'custom-extensions/animation-awaiter';

/**
 * The Genesis `Modal` webcomponent. Extends `LitElement`.
 * @see https://lit.dev/docs/components/overview/
 * @class
 * @extends {LitElement}
 */
export default class GenesisModal extends LitElement {

	/**
	 * Define customized shadow root options.
	 * @see https://lit.dev/docs/components/shadow-dom/#setting-shadowrootoptions
	 * @static
	 */
	static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true
	};

	/**
	 * Define stylesheets
	 * @see https://lit.dev/docs/components/styles/
	 * @static
	 */
	static get styles () {
		return styles;
	}

	/**
	 * Define reflector properties
	 * @see https://lit.dev/docs/components/properties/
	 * @static
	 */
	static get properties () {
		return {
			_content: {
				state: true
			},
			_title: {
				state: true
			},
			animation: {
				type: String
			},
			class: {
				type: String
			},
			closeButton: {
				attribute: 'close-button',
				type: String
			},
			direction: {
				type: String
			},
			header: {
				type: String
			},
			height: {
				type: String
			},
			isOpen: {
				attribute: 'is-open',
				type: Boolean
			},
			labeledBy: {
				attribute: 'labeled-by',
				type: String
			},
			lock: {
				type: String
			},
			overlay: {
				type: String
			},
			small: {
				type: String
			},
			staticBackdrop: {
				attribute: 'static-backdrop',
				type: String
			},
			target: {
				type: String
			},
			type: {
				type: String
			},
			width: {
				type: String
			}
		};
	}

	/**
	 * Used to determine the loaded state.
	 * @private
	 * @type {String}
	 */
	#loadedType = null;

	/**
	 * Stores the page scroll position while the modal is locked.
	 * @private
	 * @type {Number}
	 */
	#scrollLockY = 0;

	/**
	 * @private
	 * @type {import('focus-trap').FocusTrap}
	 */
	#focusTrap = null;

	/**
	 * Used to store a reference to the modal container.
	 * @private
	 * @type {Object}
	 */
	modalRef = createRef();

	/**
	 * Getter used to determine which id is represented in the "aria-labeledby" attribute.
	 * @private
	 */
	get #labeledBy () {
		return this.labeledBy || 'title';
	}

	/**
	 * The list of valid animations
	 * @type {String[]}
	 */
	animations = [
		'fade',
		'slide'
	];

	/**
	 * @constructor
	 */
	constructor () {
		super();
		this._content = '';
		this._title = '';
		this.animation = 'fade';
		this.class = '';
		this.closeButton = '';
		this.direction = 'center';
		this.header = 'true';
		this.height = '';
		this.isOpen = false;
		this.labeledBy = '';
		this.lock = 'true';
		this.overlay = 'true';
		this.small = 'false';
		this.staticBackdrop = 'false';
		this.type = 'basic';
		this.target = '';
		this.width = '';
	}

	/**
	 * Helper method to dynamically create a focus trap on the ShadowDom and slotted children nodes.
	 * @see https://github.com/focus-trap/focus-trap
	 * @returns {import('focus-trap').FocusTrap} A FocusTrap instance.
	 */
	createFocusTrap () {
		return createFocusTrap(this, {
			allowOutsideClick: true,
			checkCanFocusTrap: () => this.updateComplete,
			escapeDeactivates: false,
			tabbableOptions: {
				getShadowRoot: () => true
			}
		});
	}

	/**
	 * Logic to be ran before the {@link GenesisModal.open \.open()} method is fired.
	 * @returns {Promise<void>} A resolved promise used for the purpose of `async await` downstream.
	 */
	async beforeOpen () {
		if (this.#loadedType !== this.type) {
			// Run logic depending on which type is defined
			switch (this.type) {
				case 'ajax': {
					const response = await fetch(this.target);
					const text = await response.text();
					const virtualDom = new DOMParser().parseFromString(text, 'text/html');

					// Define reactive properties
					this._content = virtualDom.body.innerHTML;
					this._title = virtualDom.title;
					break;
				}
				default: {
					break;
				}
			}

			// Set loaded type
			this.#loadedType = this.type;
		}

		// Return dummy promise
		return Promise.resolve();
	}

	/**
	 * Event handler used for the `onload` event for the iframe element.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onIframeLoad = (e) => {

		/**
		 * Skip auto-sizing when an explicit height has been requested via the `height` attribute
		 * (e.g. a fixed-size third-party widget)
		 */
		if (this.height) {
			return;
		}

		try {
			const iframeContainer = this.modalRef.value;
			const height = e.target.contentWindow.document.getElementById('js-fans-also-liked-product').offsetHeight + 95;
			iframeContainer.style.height = `${height}px`;
		} catch (err) {
			// Cross-origin iframe content - contentWindow.document is inaccessible; fall back to CSS sizing.
		}
	}

	/**
	 * Event handler used for the `slotchange` event for the trigger slot.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onTriggerSlotChange = (e) => {
		const triggerElement = e.target.assignedNodes({flatten: true})
			.find(node => node.nodeName === 'A' || node.nodeName === 'BUTTON');

		// Assign the open event to the anchor or button element inside the trigger slot
		if (triggerElement) {
			triggerElement.addEventListener('click', this.open, false);
		}
	}

	/**
	 * Event handler used for the `keyup` event for on the Document. Used to close the modal when pressing the Escape key.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onDocumentKeyup = (e) => {
		if (e.key === 'Escape' && this.staticBackdrop !== 'true') {
			this.close();
		}
	}

	/**
	 * Event handler used for the `click` event for on the Document. Used to close the modal when clicking outside of the modal.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	onDocumentClick = (e) => {
		// eslint-disable-next-line no-extra-parens
		const path = e.path || (e.composedPath && e.composedPath()) || [];
		const contains = path?.find(element => element === this);

		if (!contains && this.staticBackdrop !== 'true') {
			this.close();
		}
	}

	/**
	 * Event handler used for the `click` event on the overlay. Used to close the modal unless `static-backdrop` is set.
	 * @returns {void}
	 */
	onOverlayClick = () => {
		if (this.staticBackdrop !== 'true') {
			this.close();
		}
	}

	/**
	 * @see https://lit.dev/docs/components/lifecycle/#firstupdated
	 * @returns {void}
	 */
	firstUpdated () {

		/**
		 * Handle the modal being declared open in markup (e.g. `is-open` set as a static attribute)
		 * rather than through the {@link GenesisModal.open \.open()} method. Without this, the modal
		 * renders visually open but skips scroll locking, the escape/outside-click listeners, and
		 * focus-trap activation.
		 */
		if (this.isOpen) {
			this.open();
		}
	}

	/**
	 * Method used programmatically open the modal window.
	 * @async
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	open = async (e) => {
		if (e) {
			e.preventDefault();
		}

		// Wait for any before open logic to run
		await this.beforeOpen();

		// Set the modal property isOpen to true (this should open the modal)
		this.isOpen = true;

		// Lock scroll if `lock` is true (position:fixed holds on iOS where overflow:hidden does not)
		if (this.lock && this.lock === 'true') {
			this.#scrollLockY = window.scrollY;
			Object.assign(document.body.style, {
				left: '0',
				overflow: 'hidden',
				position: 'fixed',
				right: '0',
				top: `-${this.#scrollLockY}px`,
				width: '100%'
			});
			document.documentElement.style.overflow = 'hidden';
		}

		// Wait for animations to finish
		await new AnimationAwaiter('#modal', {
			container: this.renderRoot,
			hasAnimation: () => this.animation && this.animations.indexOf(this.animation) > -1
		}).animationEnd;

		// Bind event to the document
		document.addEventListener('keyup', this.onDocumentKeyup, false);
		document.addEventListener('click', this.onDocumentClick, false);

		// Activate the focus trap
		this.#focusTrap = this.createFocusTrap();
		this.#focusTrap.activate();

		this.dispatchEvent(new CustomEvent('open'));
	}

	/**
	 * Method used programmatically close the modal window.
	 * @param {Event} e The Event object for the event.
	 * @returns {void}
	 */
	close = () => {
		this.renderRoot.activeElement?.blur();

		// Set the modal property isOpen to false (this should close the modal)
		this.isOpen = false;

		// Remove document event handlers
		document.removeEventListener('keyup', this.onDocumentKeyup, false);
		document.removeEventListener('click', this.onDocumentClick, false);

		// Unlock scroll if `lock` is true and restore the saved scroll position
		if (this.lock && this.lock === 'true') {
			Object.assign(document.body.style, {
				left: '',
				overflow: '',
				position: '',
				right: '',
				top: '',
				width: ''
			});
			document.documentElement.style.overflow = '';
			window.scrollTo(0, this.#scrollLockY);
		}

		// Deactivate the trap
		this.#focusTrap?.deactivate();

		this.dispatchEvent(new CustomEvent('close'));
	}

	/**
	 * @see https://lit.dev/docs/components/rendering/
	 */
	render () {

		/**
		 * Create classMap object to cleanly output the dynamic classes on the #modal div
		 * @type {import('lit/directives/class-map').ClassInfo}
		 */
		const modalDynamicClasses = {
			'has-header': this.header && this.header === 'true',
			'is-open': this.isOpen,
			'is-small': this.small && this.small === 'true',
			[`type--${this.type}`]: true,
			[`animation--${this.animation}`]: true,
			[`direction--${this.direction}`]: true,
			[`${this.class}`]: true
		};

		const containerStyle = this.width || this.height
			? `${this.width ? `max-width: ${this.width}; width: ${this.width};` : ''} ${this.height ? `height: ${this.height};` : ''}`.trim()
			: nothing;

		// Return the html template
		return html`
			<slot name="trigger" @slotchange="${this.onTriggerSlotChange}"></slot>
			<div
				role="dialog"
				aria-modal="true"
				aria-hidden="${this.isOpen ? 'false' : 'true'}"


				id="modal"
				class="g-modal${classMap(modalDynamicClasses)}"
			>
				${
					this.overlay && this.overlay === 'true'
						? html`<div class="g-modal__overlay" @click="${this.onOverlayClick}" tabindex="-1"></div>`
						: nothing
				}
				<div class="g-modal__container" role="dialog" aria-modal="true" id="container" style="${containerStyle}" ${ref(this.modalRef)}>
					${
						this.header && this.header === 'true'
							? html`<header class="g-modal__header">
								<div class="g-modal__title" id="title">
									${this._title
										? unsafeHTML(this._title)
										: html`<slot name="title" @slotchange="${this.onTitleSlotChange}"></slot>`}
								</div>
								<button type="button" @click="${this.close}" class="g-modal__close" aria-label="Close Modal"></button>
							</header>`
							: this.closeButton && this.closeButton === 'true'
								? html`<button type="button" @click="${this.close}" class="g-modal__close g-modal__close--standalone" aria-label="Close Modal"></button>`
								: nothing
					}
					<main class="g-modal__content" id="content">
						${
							this.type === 'iframe' && this.#loadedType
								? html`<iframe src="${this.target}" @load="${this.onIframeLoad}" style="overflow: hidden;">Loading...</iframe>`
								: this._content
									? unsafeHTML(this._content)
									: html`<slot name="content"></slot>`
						}
					</main>
				</div>
			</div>
		`;
	}

};
